import { useCallback, useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { ApiQueryParams } from '@/api/Api';
import {
  listWebsiteElements,
  type GetWebsiteElementListRequest,
  type GetWebsiteElementListResponse,
} from '@/api/element/list';
import {
  readWebsiteElement,
  type ReadWebsiteElementResponse,
} from '@/api/element/read';
import {
  createWebsiteElement,
  type CreateWebsiteElementResponse,
} from '@/api/element/create';
import {
  updateWebsiteElement,
  type UpdateWebsiteElementResponse,
} from '@/api/element/update';
import {
  deleteWebsiteElement,
  type DeleteWebsiteElementRequest,
  type DeleteWebsiteElementResponse,
} from '@/api/element/delete';
import {
  cloneWebsiteElement,
  type CloneWebsiteElementResponse,
} from '@/api/element/clone';
import {
  importWebsiteElements,
  type ImportWebsiteElementRequest,
  type ImportWebsiteElementResponse,
} from '@/api/element/import';
import {
  exportWebsiteElements,
  type ExportWebsiteElementRequest,
  type ExportWebsiteElementResponse,
} from '@/api/element/export';
import {
  getBulkImportStatus,
  type BulkImportStatusError,
  type BulkImportStatusResponse,
} from '@/api/element/bulkImportStatus';
import type { WebsiteElement } from '@/interfaces/element.interface';
import {
  buildParams,
  mergeApiQueryParams,
} from './utils';
import { notifyApiError, shouldSuppressErrorToast } from './error';

const ELEMENT_LIST_KEY = 'elements';
const ELEMENT_ITEM_KEY = 'element';
const BULK_IMPORT_STATUS_KEY = 'bulk-import-status';

type ElementsQueryOptions = Omit<
  UseQueryOptions<
    GetWebsiteElementListResponse,
    unknown,
    GetWebsiteElementListResponse,
    [typeof ELEMENT_LIST_KEY, GetWebsiteElementListRequest]
  >,
  'queryKey' | 'queryFn'
>;

export const useElements = (
  params?: Partial<GetWebsiteElementListRequest>,
  options?: ElementsQueryOptions
) => {
  const finalParams: GetWebsiteElementListRequest = {
    page: params?.page ?? 1,
    page_limit: params?.page_limit ?? 50,
    ...mergeApiQueryParams<GetWebsiteElementListRequest>(
      'element',
      params ?? {},
      'list'
    ),
    ...(params ?? {}),
  };

  const query = useQuery<GetWebsiteElementListResponse, unknown>({
    queryKey: [ELEMENT_LIST_KEY, finalParams],
    queryFn: async () => {
      const response = await listWebsiteElements(finalParams);
      return response.data;
    },
    ...options,
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      notifyApiError(query.error, { entity: 'element', action: 'list' });
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

export const usePrefetchWebsiteElements = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (params?: Partial<GetWebsiteElementListRequest>) => {
      const finalParams: GetWebsiteElementListRequest = {
        page: params?.page ?? 1,
        page_limit: params?.page_limit ?? 50,
        ...mergeApiQueryParams<GetWebsiteElementListRequest>(
          'element',
          params ?? {},
          'list'
        ),
        ...(params ?? {}),
      };

      try {
        await queryClient.prefetchQuery({
          queryKey: [ELEMENT_LIST_KEY, finalParams],
          queryFn: async () => {
            const response = await listWebsiteElements(finalParams);
            return response.data;
          },
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[query] Prefetch elements failed', error);
        }
      }
    },
    [queryClient]
  );
};

type ElementQueryOptions = Omit<
  UseQueryOptions<
    ReadWebsiteElementResponse,
    unknown,
    ReadWebsiteElementResponse,
    [typeof ELEMENT_ITEM_KEY, string | undefined, ApiQueryParams]
  >,
  'queryKey' | 'queryFn'
>;

export const useElement = (
  elementId?: string,
  params?: Partial<ApiQueryParams>,
  options?: ElementQueryOptions
) => {
  const finalParams = buildParams('element', params, 'read');

  const query = useQuery<ReadWebsiteElementResponse, unknown>({
    queryKey: [ELEMENT_ITEM_KEY, elementId, finalParams],
    queryFn: async () => {
      if (!elementId) {
        throw new Error('elementId is required to fetch element details');
      }
      const response = await readWebsiteElement(elementId, finalParams);
      return response.data;
    },
    ...options,
    enabled: Boolean(elementId) && (options?.enabled ?? true),
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      notifyApiError(query.error, { entity: 'element', action: 'read' });
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

export const usePrefetchWebsiteElement = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (elementId?: string, params?: Partial<ApiQueryParams>) => {
      if (!elementId) return;

      const finalParams = buildParams('element', params, 'read');

      try {
        await queryClient.prefetchQuery({
          queryKey: [ELEMENT_ITEM_KEY, elementId, finalParams],
          queryFn: async () => {
            const response = await readWebsiteElement(elementId, finalParams);
            return response.data;
          },
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[query] Prefetch element failed', error);
        }
      }
    },
    [queryClient]
  );
};

type CreateElementVariables = {
  data: WebsiteElement;
  params?: Partial<ApiQueryParams>;
};

type CreateElementOptions = UseMutationOptions<
  CreateWebsiteElementResponse,
  unknown,
  CreateElementVariables
>;

export const useCreateElement = (
  options?: CreateElementOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<CreateWebsiteElementResponse, unknown, CreateElementVariables>({
    mutationFn: async ({ data, params }) => {
      const finalParams = buildParams('element', params, 'create');
      const response = await createWebsiteElement(finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'element', action: 'create' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [ELEMENT_LIST_KEY] });
      const createdId = data?.data?.id;
      if (createdId) {
        queryClient.invalidateQueries({
          queryKey: [ELEMENT_ITEM_KEY, createdId],
        });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type UpdateElementVariables = {
  id: string;
  data: Partial<WebsiteElement>;
  params?: Partial<ApiQueryParams>;
};

type UpdateElementOptions = UseMutationOptions<
  UpdateWebsiteElementResponse,
  unknown,
  UpdateElementVariables
>;

export const useUpdateElement = (
  options?: UpdateElementOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<UpdateWebsiteElementResponse, unknown, UpdateElementVariables>({
    mutationFn: async ({ id, data, params }) => {
      const finalParams = buildParams('element', params, 'update');
      const response = await updateWebsiteElement(id, finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'element', action: 'update' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [ELEMENT_LIST_KEY] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: [ELEMENT_ITEM_KEY, variables.id],
        });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type DeleteElementVariables = {
  id: string;
  params?: Partial<DeleteWebsiteElementRequest>;
};

type DeleteElementOptions = UseMutationOptions<
  DeleteWebsiteElementResponse,
  unknown,
  DeleteElementVariables
>;

export const useDeleteElement = (
  options?: DeleteElementOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<DeleteWebsiteElementResponse, unknown, DeleteElementVariables>({
    mutationFn: async ({ id, params }) => {
      const finalParams = mergeApiQueryParams<DeleteWebsiteElementRequest>(
        'element',
        params ?? {},
        'delete'
      );
      const response = await deleteWebsiteElement(id, finalParams);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'element', action: 'delete' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [ELEMENT_LIST_KEY] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: [ELEMENT_ITEM_KEY, variables.id],
        });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type CloneElementVariables = {
  id: string;
  params?: Partial<ApiQueryParams>;
  data?: Partial<WebsiteElement>;
};

type CloneElementOptions = UseMutationOptions<
  CloneWebsiteElementResponse,
  unknown,
  CloneElementVariables
>;

export const useCloneElement = (
  options?: CloneElementOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<CloneWebsiteElementResponse, unknown, CloneElementVariables>({
    mutationFn: async ({ id, params, data }) => {
      const finalParams = buildParams('element', params, 'clone');
      const response = await cloneWebsiteElement(id, finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'element', action: 'clone' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [ELEMENT_LIST_KEY] });
      onSuccess?.(data, variables, context);
    },
  });
};

type ImportElementVariables = {
  // Can be: File object, JSON string, or parsed JSON object/array
  data: File | string | unknown;
  params?: Partial<ImportWebsiteElementRequest>;
};

type ImportElementOptions = UseMutationOptions<
  ImportWebsiteElementResponse,
  unknown,
  ImportElementVariables
>;

/**
 * Hook to import elements from JSON file
 * 
 * @example
 * // Import from File object (from file input)
 * const importMutation = useImportElement({
 *   onSuccess: () => {
 *     console.log('Elements imported successfully');
 *   }
 * });
 * 
 * const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
 *   const file = event.target.files?.[0];
 *   if (file) {
 *     importMutation.mutate({ data: file });
 *   }
 * };
 * 
 * @example
 * // Import from JSON string or object
 * importMutation.mutate({
 *   data: { elements: [...] } // or JSON string
 * });
 */
export const useImportElement = (
  options?: ImportElementOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<ImportWebsiteElementResponse, unknown, ImportElementVariables>({
    mutationFn: async ({ data, params }) => {
      const finalParams = buildParams('element', params, 'import');
      const response = await importWebsiteElements(finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'element', action: 'import' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [ELEMENT_LIST_KEY] });
      onSuccess?.(data, variables, context);
    },
  });
};

type ExportElementVariables = {
  params?: Partial<ExportWebsiteElementRequest>;
  // Optional filename for downloaded JSON file
  filename?: string;
  // Whether to automatically trigger download
  autoDownload?: boolean;
};

type ExportElementOptions = UseMutationOptions<
  ExportWebsiteElementResponse | Blob,
  unknown,
  ExportElementVariables
>;

type BulkImportStatusQueryOptions = Omit<
  UseQueryOptions<
    BulkImportStatusResponse | BulkImportStatusError,
    unknown,
    BulkImportStatusResponse | BulkImportStatusError,
    [typeof BULK_IMPORT_STATUS_KEY, string]
  >,
  'queryKey' | 'queryFn'
>;

export const useBulkImportStatus = (
  bulkImportId: string | undefined,
  options?: BulkImportStatusQueryOptions
) => {
  const query = useQuery<
    BulkImportStatusResponse | BulkImportStatusError,
    unknown
  >({
    queryKey: [BULK_IMPORT_STATUS_KEY, bulkImportId || ''],
    queryFn: async () => {
      if (!bulkImportId) {
        throw new Error('bulkImportId is required');
      }
      const response = await getBulkImportStatus(bulkImportId);
      return response.data;
    },
    ...options,
    enabled: Boolean(bulkImportId) && (options?.enabled ?? true),
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      if (import.meta.env.DEV) {
        console.warn('[query] Bulk import status check failed', query.error);
      }
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

/**
 * Hook to export elements as JSON file
 * 
 * @example
 * // Export all elements for a specific interface
 * const exportMutation = useExportElement({
 *   onSuccess: (data) => {
 *     // data is already downloaded as JSON file
 *   }
 * });
 * 
 * exportMutation.mutate({
 *   params: {
 *     filters: [{ field: 'website_interface_id', value: '123' }]
 *   },
 *   filename: 'elements-export.json',
 *   autoDownload: true
 * });
 */
export const useExportElement = (
  options?: ExportElementOptions
) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<ExportWebsiteElementResponse | Blob, unknown, ExportElementVariables>({
    mutationFn: async ({ params, filename, autoDownload = true }) => {
      // Extract filters to preserve them (buildParams doesn't include filters)
      const { filters, ...paramsWithoutFilters } = params ?? {};
      const baseParams = buildParams('element', paramsWithoutFilters, 'export');
      // Add filters back to the params
      const finalParams = {
        ...baseParams,
        ...(filters ? { filters } : {}),
      };
      
      // Request as blob for file download
      const response = await exportWebsiteElements({
        ...finalParams,
        responseType: 'blob',
      });

      // If the response is a blob, handle file download
      if (response.data instanceof Blob) {
        if (autoDownload) {
          const blob = response.data;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename || `elements-export-${Date.now()}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
        return response.data;
      }

      // If response is JSON, convert to blob and download
      if (autoDownload && response.data) {
        const jsonString = JSON.stringify(response.data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || `elements-export-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        return blob;
      }

      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'element', action: 'list' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  });
};

